/**
 * TierSelector — Basic/Deluxe tier toggle for RTE listing page.
 *
 * Displays tier options with pricing, item counts, and visual toggle.
 * Selected tier affects the price shown in the booking panel and
 * determines which items appear in the KitContents component.
 *
 * Spec: §5 Tier Data Model
 */
import React from 'react';
import classNames from 'classnames';

import { useIntl } from '../../util/reactIntl';
import { formatMoney } from '../../util/currency';
import { calculateTotalPrice } from '../../util/pricing';
import { types } from '../../util/sdkLoader';

import { NamedLink } from '../../components';

import css from './TierSelector.module.css';

/**
 * All-in disclosure shown under the tier price(s) — owner request
 * 2026-07-03. One quiet line so renters know the displayed price already
 * includes the platform fee and damage protection; no separate fee at
 * checkout. Links to the FAQ Fees & Protection section. The tier cards
 * are the price surface renters actually see (desktop listing panel and
 * the mobile booking modal), so the line lives here to sit directly
 * beneath them on every booking listing.
 */
const PriceDisclosure = () => (
  <p className={css.priceDisclosure}>
    <span className={css.priceDisclosureIcon} aria-hidden="true">🛡️</span>
    <span>
      Includes all platform fees and damage protection. No surprises at checkout.{' '}
      <NamedLink name="FAQPage" to={{ hash: '#fees' }} className={css.priceDisclosureLink}>
        Learn more
      </NamedLink>
    </span>
  </p>
);

const { Money } = types;

const TierSelector = props => {
  const {
    tiers,
    selectedTierKey,
    onSelectTier,
    currency,
    guestCount,
    minGuests = 1,
    maxGuests = 100,
    rootClassName,
    className,
  } = props;

  const intl = useIntl();
  const classes = classNames(rootClassName || css.root, className);

  if (!tiers || Object.keys(tiers).length === 0) return null;

  const tierEntries = Object.entries(tiers);

  // Compute the per-day display string for a single tier. Shared between
  // the multi-tier toggle cards and the single-tier compressed card so
  // both pick up the same fee + currency formatting.
  const formatPriceForTier = tier => {
    if (
      typeof tier.basePricePerDay === 'number' &&
      typeof tier.perGuestPricePerDay === 'number'
    ) {
      if (typeof guestCount === 'number' && guestCount > 0) {
        const totalPriceInSubunits = calculateTotalPrice(
          tier.basePricePerDay,
          tier.perGuestPricePerDay,
          guestCount
        );
        return formatMoney(intl, new Money(totalPriceInSubunits, currency));
      }
      const minPriceInSubunits = calculateTotalPrice(
        tier.basePricePerDay,
        tier.perGuestPricePerDay,
        minGuests
      );
      const maxPriceInSubunits = calculateTotalPrice(
        tier.basePricePerDay,
        tier.perGuestPricePerDay,
        maxGuests
      );
      const minPrice = formatMoney(intl, new Money(minPriceInSubunits, currency));
      const maxPrice = formatMoney(intl, new Money(maxPriceInSubunits, currency));
      return `From ${minPrice}–${maxPrice}`;
    }
    if (typeof tier.pricePerDay === 'number' && !isNaN(tier.pricePerDay) && tier.pricePerDay > 0) {
      // Show all-in price (base + 15% customer fee)
      const allInPrice = Math.round(tier.pricePerDay * 1.15);
      return formatMoney(intl, new Money(allInPrice, currency));
    }
    return '';
  };

  // Single tier: render a compressed read-only card per design §6.1.
  // Drops the tier name "label" chip (no BASIC/DELUXE-style selectable
  // affordance for a non-choice) and shows just price + /day, matching
  // the visual rhythm of the multi-tier card so listings read consistently.
  if (tierEntries.length === 1) {
    const [, tier] = tierEntries[0];
    const priceDisplay = formatPriceForTier(tier);
    if (!priceDisplay) return null;
    return (
      <div className={classes}>
        <div className={css.singleTierCard}>
          <div className={css.singleTierLabel}>Pricing</div>
          <div className={css.singleTierPrice}>
            {priceDisplay}
            <span className={css.perDay}> per day</span>
          </div>
        </div>
        <PriceDisclosure />
      </div>
    );
  }

  // On initial render, parent state (selectedTierKey) is null until a
  // useEffect on ListingPageCarousel hydrates it — but that effect
  // doesn't refire when tiers load asynchronously. Mirror the
  // SectionPackageContents fallback (`selectedTierKey || Object.keys
  // (tiers)[0]`) so the visual highlight matches the default
  // inclusions display.
  const effectiveSelectedKey = selectedTierKey || tierEntries[0][0];

  return (
    <div className={classes}>
      <div className={css.tierOptions}>
        {tierEntries.map(([key, tier]) => {
          const isSelected = key === effectiveSelectedKey;
          const priceDisplay = formatPriceForTier(tier);

          return (
            <button
              key={key}
              className={classNames(css.tierOption, {
                [css.tierOptionSelected]: isSelected,
              })}
              onClick={() => onSelectTier(key)}
              type="button"
            >
              <div className={css.tierLabel}>{tier.name}</div>
              <div className={css.tierPrice}>
                {priceDisplay}
                <span className={css.perDay}> per day</span>
              </div>
            </button>
          );
        })}
      </div>
      <PriceDisclosure />
    </div>
  );
};

export default TierSelector;
